import { motion } from 'framer-motion';
import { HeartHandshake, CalendarHeart, Users, Play } from 'lucide-react';
import { SOCIAL } from '../data/site';

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.7, delay },
});

const PILLARS = [
  { icon: CalendarHeart, title: 'Every Month', desc: 'A standing commitment — Annadana food donation drives held month after month, not once a year.' },
  { icon: HeartHandshake, title: 'With the Trust', desc: 'Conducted alongside the Dr. Puneeth Rajkumar Charitable Trust, carrying forward Appu\u2019s spirit of giving.' },
  { icon: Users, title: 'For the Community', desc: 'Hot meals served to hundreds of families, patients and attenders around government hospitals.' },
];

export default function SocialWorks() {
  return (
    <main data-testid="social-works-page">
      <section className="pt-40 pb-16 lg:pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-6">CSR ACTIVITIES — EVERY MONTH, ON THE GROUND</p>
          <motion.h1
            initial={{ opacity: 0, y: 34 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading font-extrabold tracking-tighter leading-[0.95] text-5xl sm:text-7xl text-ink max-w-4xl"
            data-testid="social-headline"
          >
            Building homes.<br />
            <span className="font-serif italic font-medium text-terra">Feeding</span> hope.
          </motion.h1>
          <motion.p {...fade(0.15)} className="mt-8 text-base lg:text-lg text-ink/60 leading-relaxed max-w-3xl" data-testid="social-intro">
            Every month, the Prakruti team joins the <span className="font-medium text-ink">{SOCIAL.partner}</span> for
            Annadana — free food donation drives for families, patients and attenders. For us, leading a better
            future starts long before the first foundation stone.
          </motion.p>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-stone border-y border-clay" data-testid="social-pillars">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-clay border border-clay">
          {PILLARS.map((p, i) => (
            <motion.div key={p.title} {...fade(i * 0.08)} className="bg-sand p-8 lg:p-10" data-testid={`social-pillar-${i}`}>
              <span className="w-11 h-11 bg-terra/10 text-terra flex items-center justify-center"><p.icon size={18} /></span>
              <h3 className="mt-6 font-heading font-bold text-xl tracking-tight text-ink">{p.title}</h3>
              <p className="mt-3 text-sm text-ink/60 leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-16 lg:py-24" data-testid="social-gallery">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-4">ON THE GROUND</p>
          <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-ink mb-12">Annadana with the {SOCIAL.partner}</h2>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
            {SOCIAL.images.map((img, i) => (
              <motion.figure
                key={img.src} {...fade((i % 3) * 0.08)}
                className="group relative mb-5 break-inside-avoid overflow-hidden bg-stone"
                data-testid={`social-img-${i}`}
              >
                <img src={img.src} alt={img.caption} loading="lazy"
                  className="w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-bark/80 to-transparent px-5 pt-12 pb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                  <span className="text-xs text-sand">{img.caption}</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="social-videos">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-4">WATCH</p>
          <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-ink mb-12">From the field</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {SOCIAL.videos.map((v, i) => (
              <motion.div key={v.src} {...fade(i * 0.1)} className="border border-clay bg-stone" data-testid={`social-video-${i}`}>
                <video controls preload="none" poster={v.poster} className="w-full aspect-video object-cover bg-bark">
                  <source src={v.src} type="video/mp4" />
                </video>
                <p className="px-6 py-5 text-sm text-ink/60 flex items-center gap-2">
                  <Play size={14} className="text-terra" /> {v.caption}
                </p>
              </motion.div>
            ))}
          </div>
          <motion.p {...fade(0.2)} className="mt-16 text-center font-serif italic text-2xl sm:text-3xl text-ink/60 max-w-2xl mx-auto leading-snug">
            “Your Trust. Our Commitment. A Better Future — for everyone.”
          </motion.p>
        </div>
      </section>
    </main>
  );
}
